/**
 * Persistente System-Notification "Du bist eingestempelt".
 *
 * Strategie:
 *  - Beim Einstempeln zeigen wir über den Service-Worker eine Notification
 *    mit `requireInteraction: true` und einem "Ausstempeln"-Action-Button.
 *    Diese Notification bleibt sichtbar bis der User interagiert oder sie
 *    aktiv geschlossen wird — auch wenn die App geschlossen ist.
 *  - Beim Ausstempeln wird sie über den `tag`-Identifier wieder dismissed.
 *  - Beim App-Start prüfen wir, ob noch eine aktive Open-End-Schicht
 *    existiert und re-zeigen die Notification (Re-Start-Robustheit).
 *
 * Voraussetzung: Notification-Permission ist `granted` (per PushToggle
 * im Profil aktiviert).
 */

const TAG = 'shiftsync.clocked-in';

function hasPermission(): boolean {
  if (typeof window === 'undefined') return false;
  if (!('Notification' in window)) return false;
  return Notification.permission === 'granted';
}

async function getServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const reg = await navigator.serviceWorker.ready;
    return reg;
  } catch {
    return null;
  }
}

interface ClockInShift {
  id: string;
  startTime: string; // "HH:MM"
}

/**
 * Zeigt (oder aktualisiert) die "eingestempelt"-Notification.
 * Durch den `tag` wird eine eventuell bereits sichtbare Notification ersetzt,
 * nicht dupliziert.
 */
export async function showClockedInNotification(
  shift: ClockInShift,
  locationName: string,
): Promise<void> {
  if (!hasPermission()) return;
  const reg = await getServiceWorker();
  if (!reg) return;

  // NotificationOptions in der DOM-Lib kennt `actions` nicht offiziell,
  // wird aber von Chromium/Android trotzdem ausgewertet.
  const options = {
    body: `${locationName} · seit ${shift.startTime}`,
    tag: TAG,
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-192.png',
    requireInteraction: true,
    // Erste Anzeige still — kein Klingeln, nur in der Statusleiste landen
    silent: true,
    actions: [{ action: 'clockout', title: 'Ausstempeln' }],
    data: { url: '/', kind: 'clocked-in', shiftId: shift.id },
  } as NotificationOptions;

  try {
    await reg.showNotification('Du bist eingestempelt', options);
  } catch {
    // Ignorieren — kein Showstopper, die App selbst zeigt die Statusleiste
  }
}

/** Entfernt die "eingestempelt"-Notification, falls sie sichtbar ist. */
export async function dismissClockedInNotification(): Promise<void> {
  const reg = await getServiceWorker();
  if (!reg) return;
  try {
    const notifications = await reg.getNotifications({ tag: TAG });
    for (const n of notifications) n.close();
  } catch {
    // ignorieren
  }
}
